/**
 * Per-scenario access on the SE-8354 contract (SE-8356, PV16).
 *
 * `listScenarios` now answers only the scenarios the caller owns, is granted
 * or that are legacy no-owner, and every row carries `myRole`,
 * `canManageSharing`, `financialsVisible` and `revision`. One small query
 * (`SquadPlanningListScenarioAccess`) reads those fields for every row so the
 * pill bar can gate rename / delete per pill and the Pitch can gate Add /
 * Remove / formation / Share for the active scenario — one request, not one
 * per pill. Apollo normalizes `ScenarioModel` by id, so the Financials switch
 * mutation (which returns the same selection) updates these fields in place.
 *
 * Until SE-8354 is deployed the query is refused by the schema; the hook then
 * reports `null` for every scenario and the surfaces keep today's behaviour
 * (see `resolveScenarioPermissions`).
 * @module features/squad-planning/hooks/useScenarioAccess
 */
import { useMemo } from "react";

import { useSquadPlanningListScenarioAccessQuery } from "@/generated/graphql";
import type { ScenarioRoleValue } from "@/features/squad-planning/utils/scenarioAccess";

/** The access fields of one scenario as the API sent them. */
export interface ScenarioAccess {
  readonly myRole: ScenarioRoleValue;
  readonly canManageSharing: boolean;
  readonly financialsVisible: boolean;
  readonly revision: number;
}

/** Access by scenario id, for every scenario the caller can read. */
export type ScenarioAccessMap = ReadonlyMap<string, ScenarioAccess>;

const EMPTY: ScenarioAccessMap = new Map();

/**
 * Reads the access fields of every scenario the caller can see.
 * @returns The access map (empty until the query answers or when refused)
 */
export function useScenarioAccessMap(): ScenarioAccessMap {
  // The store's createScenario / deleteScenario write the LIST query with
  // rows that carry no access fields, which leaves this read partial until
  // the refetch lands. Partial data is accepted so the rows already known
  // stay known through that beat; a row without a role is simply absent
  // (today's affordances) rather than blanking the whole map.
  const { data } = useSquadPlanningListScenarioAccessQuery({
    fetchPolicy: "cache-and-network",
    returnPartialData: true,
  });
  return useMemo((): ScenarioAccessMap => {
    const rows = data?.listScenarios;
    if (rows == null) return EMPTY;
    return new Map(
      rows.flatMap(row =>
        typeof row?.myRole === "string"
          ? [
              [
                row.id,
                {
                  myRole: row.myRole,
                  canManageSharing: row.canManageSharing,
                  financialsVisible: row.financialsVisible,
                  revision: row.revision,
                },
              ] as const,
            ]
          : []
      )
    );
  }, [data]);
}

/**
 * Reads the access fields of one scenario.
 * @param scenarioId - The scenario in view, or null for Current Squad
 * @returns Its access, or null while unknown / not readable
 */
export function useScenarioAccess(
  scenarioId: string | null
): ScenarioAccess | null {
  const map = useScenarioAccessMap();
  return scenarioId == null ? null : (map.get(scenarioId) ?? null);
}
